import { Download, Info, Monitor, Share, Smartphone, WifiOff, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import { requestPwaInstall, usePwaState } from './install';
import styles from './PwaInstall.module.css';

type Platform = 'ios' | 'android' | 'desktop';

function currentPlatform(): Platform {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  return /Android/i.test(navigator.userAgent) ? 'android' : 'desktop';
}

const content = {
  ru: {
    button: 'Установить приложение',
    info: 'О приложении',
    eyebrow: 'Уроки всегда под рукой',
    title: 'Занимайтесь с телефона',
    description: 'Добавьте Ustaz на главный экран: уроки и личный кабинет будут открываться по одному нажатию.',
    installed: 'Приложение уже установлено. Открывайте его с главного экрана.',
    close: 'Закрыть инструкцию',
    native: 'Установить сейчас',
    installing: 'Открываем установку…',
    fallback: 'Если кнопки установки нет, воспользуйтесь инструкцией для вашего устройства.',
    accepted: 'Установка запрошена. Дождитесь значка Ustaz на главном экране или в списке приложений.',
    dismissed: 'Установка отменена. Позже можно установить приложение через меню браузера.',
    unavailable: 'Не удалось открыть установку. Воспользуйтесь инструкцией ниже.',
    ios: ['Откройте suleimen-ustaz.com в Safari на iPhone или iPad.', 'Нажмите «Поделиться» — значок квадрата со стрелкой вверх.', 'Выберите «На экран Домой». Если есть переключатель «Открывать как веб-приложение», включите его, затем нажмите «Добавить».'],
    android: ['Откройте suleimen-ustaz.com в Chrome на телефоне.', 'Откройте меню ⋮ в правом верхнем углу.', 'Выберите «Установить приложение» или «Добавить на главный экран» и подтвердите установку.'],
    desktop: ['Откройте сайт в Chrome или Edge.', 'Нажмите значок установки в адресной строке или выберите установку приложения в меню браузера.', 'Продолжайте учиться с телефона: откройте тот же сайт и войдите в свой аккаунт.'],
    internet: 'Для видеоуроков, тестов, оплаты и проверки доступа нужен интернет. Установка не скачивает уроки на телефон.',
    offline: 'Нет интернета. Для уроков и оплаты нужно подключение.',
    tabLabel: 'Инструкция для устройства',
  },
  kk: {
    button: 'Қолданбаны орнату',
    info: 'Қолданба туралы',
    eyebrow: 'Сабақтар әрдайым қолыңызда',
    title: 'Телефоннан оқыңыз',
    description: 'Ustaz қолданбасын басты экранға қосыңыз: сабақтар мен жеке кабинет бір рет басқанда ашылады.',
    installed: 'Қолданба орнатылған. Оны басты экраннан ашыңыз.',
    close: 'Нұсқаулықты жабу',
    native: 'Қазір орнату',
    installing: 'Орнату ашылуда…',
    fallback: 'Орнату батырмасы болмаса, құрылғыңызға арналған нұсқаулықты қолданыңыз.',
    accepted: 'Орнату сұралды. Басты экранда немесе қолданбалар тізімінде Ustaz белгішесінің пайда болуын күтіңіз.',
    dismissed: 'Орнатудан бас тартылды. Кейін браузер мәзірі арқылы орнатуға болады.',
    unavailable: 'Орнатуды ашу мүмкін болмады. Төмендегі нұсқаулықты қолданыңыз.',
    ios: ['iPhone немесе iPad құрылғысында suleimen-ustaz.com сайтын Safari арқылы ашыңыз.', 'Жоғары бағытталған көрсеткісі бар шаршы — «Бөлісу» белгішесін басыңыз.', '«Басты экранға қосу» тармағын таңдаңыз. «Веб-қолданба ретінде ашу» тетігі болса, оны қосып, «Қосу» батырмасын басыңыз.'],
    android: ['Телефонда suleimen-ustaz.com сайтын Chrome арқылы ашыңыз.', 'Оң жақ жоғарғы бұрыштағы ⋮ мәзірін ашыңыз.', '«Қолданбаны орнату» немесе «Басты экранға қосу» тармағын таңдап, орнатуды растаңыз.'],
    desktop: ['Сайтты Chrome немесе Edge арқылы ашыңыз.', 'Мекенжай жолағындағы орнату белгішесін басыңыз немесе браузер мәзіріндегі қолданбаны орнату тармағын таңдаңыз.', 'Телефоннан оқуды жалғастыру үшін осы сайтты ашып, өз аккаунтыңызға кіріңіз.'],
    internet: 'Бейнесабақтар, тесттер, төлем және қолжетімділікті тексеру үшін интернет қажет. Орнату сабақтарды телефонға жүктемейді.',
    offline: 'Интернет жоқ. Сабақтар мен төлем үшін байланыс қажет.',
    tabLabel: 'Құрылғыға арналған нұсқаулық',
  },
} as const;

export function PwaInstall() {
  const { locale } = useLocale();
  const { installed, canInstall, installing, result, online } = usePwaState();
  const dialog = useRef<HTMLDialogElement>(null);
  const [platform, setPlatform] = useState<Platform>(currentPlatform);
  const text = content[locale];

  return (
    <>
      <aside className={styles.widget}>
        {!online && <p className={styles.offline} role="status"><WifiOff aria-hidden="true" />{text.offline}</p>}
        <button className={`${styles.launcher} ${installed ? styles.infoOnly : ''}`} type="button" onClick={() => dialog.current?.showModal()} aria-label={installed ? text.info : text.button} aria-haspopup="dialog">
          <Info aria-hidden="true" />
          {!installed && <span>{text.button}</span>}
        </button>
      </aside>

      <dialog ref={dialog} className={styles.dialog} aria-labelledby="pwa-install-title" onClick={(event) => {
        if (event.target === dialog.current) dialog.current.close();
      }}>
        <div className={styles.content}>
          <button type="button" className={styles.close} aria-label={text.close} onClick={() => dialog.current?.close()}><X aria-hidden="true" /></button>
          <div className={styles.brand} aria-hidden="true">SA</div>
          <p className={styles.eyebrow}>{text.eyebrow}</p>
          <h2 id="pwa-install-title">{text.title}</h2>
          <p className={styles.description}>{installed ? text.installed : text.description}</p>

          {!installed && (canInstall || installing) && <button type="button" className={styles.install} disabled={installing} onClick={() => { void requestPwaInstall(); }}><Download aria-hidden="true" />{installing ? text.installing : text.native}</button>}
          {result && !installed && <p className={styles.feedback} role="status">{text[result]}</p>}
          {!installed && !canInstall && !installing && !result && <p className={styles.hint}>{text.fallback}</p>}

          <div className={styles.tabs} role="group" aria-label={text.tabLabel}>
            <button type="button" aria-pressed={platform === 'ios'} onClick={() => setPlatform('ios')}><Share aria-hidden="true" />iPhone / iPad</button>
            <button type="button" aria-pressed={platform === 'android'} onClick={() => setPlatform('android')}><Smartphone aria-hidden="true" />Android</button>
            <button type="button" aria-pressed={platform === 'desktop'} onClick={() => setPlatform('desktop')}><Monitor aria-hidden="true" />PC / Mac</button>
          </div>
          <ol className={styles.steps}>{text[platform].map((step) => <li key={step}>{step}</li>)}</ol>
          <p className={styles.note}><WifiOff aria-hidden="true" /><span>{text.internet}</span></p>
        </div>
      </dialog>
    </>
  );
}
